import React from 'react';
import { Link } from 'react-router-dom';
import TypeBadge from './TypeBadge';
import AssetCover from './AssetCover';

function Stars({ rating = 0, count = 0 }) {
  const full = Math.round(rating);
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-2)' }}>
      <span style={{ letterSpacing: -1, color: '#F59E0B' }}>
        {'★'.repeat(full)}{'☆'.repeat(5 - full)}
      </span>
      <span style={{ fontFamily: 'Space Mono', fontSize: 11, color: 'var(--text-2)' }}>
        {rating.toFixed(1)}
      </span>
      <span style={{ color: 'var(--text-3)' }}>({count})</span>
    </span>
  );
}

export default function AssetCard({ asset, onTagClick }) {
  if (!asset) return null;
  const owner = asset.ownerId || {};
  const stats = asset.stats || {};
  const isFree = asset.price === 0;

  return (
    <Link to={`/asset/${asset._id}`} style={{ textDecoration: 'none', display: 'flex', height: '100%' }}>
      <article style={{
        display: 'flex', flexDirection: 'column', width: '100%',
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden', transition: 'box-shadow 0.2s, border-color 0.2s',
        cursor: 'pointer',
      }}
        onMouseOver={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.borderColor = '#CBD5E1'; }}
        onMouseOut={e => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
      >
        {/* Cover */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <AssetCover assetType={asset.assetType} previewImage={asset.previewImage} height={140} />
          <div style={{ position: 'absolute', top: 10, left: 10 }}>
            <TypeBadge type={asset.assetType} size="sm" />
          </div>
          {isFree && (
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: '#F0FDF4', color: '#16A34A', border: '1px solid #bbf7d0' }}>Free</span>
            </div>
          )}
        </div>

        {/* Body — flex grow so all cards same height */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '14px 16px 16px' }}>
          {/* Title */}
          <h3 style={{
            fontSize: 14, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4,
            marginBottom: 6, overflow: 'hidden',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {asset.title}
          </h3>

          {/* Short desc */}
          <p style={{
            fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, margin: '0 0 10px',
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            flex: 1,
          }}>
            {asset.shortDescription}
          </p>

          {/* Language tags */}
          {asset.languages?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
              {asset.languages.slice(0, 5).map(l => (
                <span key={l} style={{
                  fontSize: 10, padding: '1px 6px', borderRadius: 3,
                  background: 'var(--accent-bg)', color: 'var(--accent)',
                  fontFamily: 'Space Mono', fontWeight: 700, textTransform: 'uppercase',
                }}>{l}</span>
              ))}
            </div>
          )}

          {/* Clickable tags */}
          {asset.tags?.length > 0 && onTagClick && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
              {asset.tags.slice(0, 4).map(tag => (
                <button
                  key={tag}
                  onClick={e => { e.preventDefault(); e.stopPropagation(); onTagClick(tag); }}
                  style={{
                    fontSize: 10, padding: '2px 7px', borderRadius: 4, cursor: 'pointer',
                    background: 'var(--surface2)', color: 'var(--text-2)',
                    border: '1px solid var(--border)', fontFamily: 'inherit', fontWeight: 500,
                    transition: 'all 0.15s',
                  }}
                  onMouseOver={e => { e.currentTarget.style.background = 'var(--accent-bg)'; e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.borderColor = '#BFDBFE'; }}
                  onMouseOut={e => { e.currentTarget.style.background = 'var(--surface2)'; e.currentTarget.style.color = 'var(--text-2)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}

          {/* Footer: price + rating + seller */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{
                fontSize: 15, fontWeight: 700, fontFamily: 'Space Mono',
                color: isFree ? 'var(--success)' : 'var(--text)',
              }}>
                {isFree ? 'Free' : `$${asset.price}`}
              </span>
              <Stars rating={stats.rating || 0} count={stats.reviewCount || 0} />
            </div>

            {/* Seller avatar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {owner.avatar
                ? <img src={owner.avatar} alt="" style={{ width: 22, height: 22, borderRadius: '50%', border: '1px solid var(--border)' }} />
                : <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--accent-bg)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>
                    {(owner.name || 'U')[0]}
                  </div>
              }
              <span style={{ fontSize: 11, color: 'var(--text-2)', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {owner.name}
              </span>
              {owner.sellerStats?.isVerifiedSeller && (
                <svg width="12" height="12" viewBox="0 0 10 10" fill="var(--success)">
                  <path d="M4.5 8.5L1 5l1-1 2.5 2.5 4.5-4.5 1 1z"/>
                </svg>
              )}
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
}
